import { ISearchResponse } from "../interfaces/VehicleSearch";

// api3 answers a failed search with {"status":"success","data":[]} — every exception inside
// AvailableVehicleFinder is swallowed and reported as an empty-but-successful result set. The
// callers then read .vehicles off an array and blow up on a TypeError deep inside the search,
// which surfaces as the generic "error fetching results" with no clue what went wrong.
//
// Fail loudly and specifically instead: a response without a vehicles array is a broken search,
// not a search with no results, and the two must never look the same to the user.
export class MalformedSearchResponseError extends Error {
  constructor(received: unknown) {
    super(`Search response has no vehicles array (received ${describe(received)})`);
    this.name = "MalformedSearchResponseError";
  }
}

const describe = (value: unknown): string => {
  if (value === null || value === undefined) return String(value);
  if (Array.isArray(value)) return `array of ${value.length}`;
  if (typeof value === "object") return `object with keys [${Object.keys(value as object).join(", ")}]`;
  return typeof value;
};

/**
 * Narrow a raw search payload to ISearchResponse, or throw.
 *
 * Only `vehicles` is required: `count`, `onsales` and `extraOnsales` are legitimately absent in
 * some responses and the callers default them.
 */
export const assertSearchResponse = (response: unknown): ISearchResponse => {
  const candidate = response as ISearchResponse | undefined;

  if (!candidate || Array.isArray(candidate) || !Array.isArray(candidate.vehicles)) {
    throw new MalformedSearchResponseError(response);
  }

  return candidate;
};
